<template>
  <v-card outlined class="email-message-card">
    <div v-if="openReplyBox" class="message-box">
      <v-combobox
        v-model="composeData.to"
        multiple
        chips
        small-chips
        deletable-chips
        hide-selected
        hide-details
        item-text="name"
        item-value="email"
        class="pt-0"
        solo
        flat
      >
        <!-- Slot: Option -->
        <template #item="{ item: option }">
          <v-avatar size="22" class="me-2">
            <v-img :src="option.avatar"></v-img>
          </v-avatar>
          <span class="text-sm">{{ option.name }}</span>
        </template>

        <!-- Slot: Prepend Inner -->
        <template #prepend-inner>
          <span class="text--primary">To:</span>
        </template>
      </v-combobox>
      <vue-editor
        ref="quillEditor"
        v-model="composeData.message"
        placeholder="Message"
        :editor-toolbar="customToolbar"
      ></vue-editor>
      <div class="my-2">
        <v-btn class="mx-2" outlined small :loading="loading" @click="replyMessage">Send</v-btn>
        <v-btn small outlined @click="discard">Discard</v-btn>
      </div>

      <v-divider></v-divider>
    </div>
    <div v-if="message.from && message.from.length" class="pa-5 d-flex flex-wrap align-center">
      <v-avatar size="38" class="me-3">
        <v-img :src="require('@/assets/images/avatars/1.png')"></v-img>
      </v-avatar>
      <div class="d-flex justify-center flex-column">
        <span class="text--primary font-weight-medium text-base">{{ message.from[0].name }}</span>
        <span class="text-xs3">To: {{ getRecipients(message.recipients) }}</span>
      </div>

      <v-spacer></v-spacer>

      <div class="my-1">
        <span class="text-xs me-2 text-no-wrap">{{
          formatDate(message.createdAt, { dateStyle: 'long', timeStyle: 'short' })
        }}</span>
        <!-- <v-btn icon small class="me-1">
          <v-icon size="22">
            {{ icons.mdiAttachment }}
          </v-icon>
        </v-btn> -->
      </div>
    </div>

    <v-divider></v-divider>

    <!-- eslint-disable vue/no-v-html -->
    <div class="message-content ma-5" v-html="message.body"></div>

    <template v-if="message.files && message.files.length">
      <v-divider></v-divider>

      <div class="mx-5 my-3">
        <p class="mb-2">Attachments</p>
        <v-chip
          v-for="(attachment, index) in message.files"
          :key="index"
          target="_blank"
          class="me-2"
          tag="a"
          outlined
          download
        >
          <!-- <v-img :src="attachment.thumbnail" width="16px" class="me-2" /> -->
          <span class="text-xs align-text-top" @click="download(attachment)">{{
            attachment.filename
          }}</span>
        </v-chip>
      </div>
    </template>
    <div v-if="isVisible" class="pa-5 pb-2">
      <v-btn color="grey" class="mr-2" outlined small @click="reply"> Reply </v-btn>
      <v-btn color="secondary" outlined small @click="replyAll">Reply All</v-btn>
    </div>
  </v-card>
</template>

<script>
import { formatDate } from '@core/utils/filter'
import { VueEditor } from 'vue2-editor'
import { ref, computed } from '@vue/composition-api'
// eslint-disable-next-line object-curly-newline
import {
  mdiAttachment,
  mdiStarOutline,
  mdiStar,
  mdiDotsVertical,
  mdiReplyOutline,
  mdiReplyAllOutline,
  mdiForward,
} from '@mdi/js'
import { storeActions, storeGetters } from '@/store/storeConstants'
import store from '@/store'
import Toaster from '@/@core/utils/sweetToast'
import { ERROR_RESPONSE } from '@/constants/appConstants'
import useEmailService from '@/components/email/useEmailService'
import { useRouter } from '@core/utils'

export default {
  components: {
    VueEditor,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    propType: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const openReplyBox = ref(false)
    const composeData = ref({})
    const loading = ref(false)
    const { sendEmail } = useEmailService()
    const { route } = useRouter()
    const token = computed(() => {
      return store.getters[`auth/${storeGetters.GET_EMAIL_TOKEN}`]
    })
    // Single Message Actions
    const singleMessageActions = [
      { title: 'Reply', icon: mdiReplyOutline },
      { title: 'Reply All', icon: mdiReplyAllOutline },
      { title: 'Forward', icon: mdiForward },
    ]
    const customToolbar = [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
    ]

    const reply = () => {
      const from = props.message.recipients.map(opt => {
        return opt.recipient?.email
      })
      composeData.value.to = from
      openReplyBox.value = true
    }

    const replyAll = () => {
      const from = props.message.from.map(opt => {
        return opt.email
      })
      const cc = props.message.recipients.map(opt => {
        return opt.recipient.email
      })
      composeData.value.to = [...from, ...cc]
      openReplyBox.value = true
    }

    const discard = () => {
      composeData.value = {}
      openReplyBox.value = false
    }

    const getFormatPayload = arr => {
      if (!Array.isArray(arr)) return []

      return arr.map(opt => {
        return {
          email: opt,
        }
      })
    }

    const replyMessage = () => {
      loading.value = true
      const payLoad = {
        component: 'email/send',
        object: {
          to: getFormatPayload(composeData.value.to),
          message: composeData.value.message,
          reply_to_message_id: props.message.message_id,
          subject: props.message.subject,
          prop_type: props.propType,
          prop_id: route.value.params.uuid,
        },
      }

      sendEmail(payLoad)
        .then(({ data }) => {
          emit('fetchRecord')
          discard()
          Toaster.success(data.message)
        })
        .catch((err) => {
          console.log('[213] =>', err)
          Toaster.error(ERROR_RESPONSE, 'warning')
        })
        .finally(() => {
          openReplyBox.value = false
          loading.value = false
        })
    }

    const isVisible = computed(() => {
      if (
        route.value.params.folder === 'inbox' ||
        !route.value.params.folder ||
        route.value.params.folder === 'sent'
      ) {
        return true
      }

      return false
    })

    const download = attach => {
      const payLoad = {
        component: `email/client/attachments/${attach.id}/download`,
      }

      store
        .dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payLoad)
        .then(({ data }) => {
          if (data.success) {
            Toaster.success(data.message)
          } else {
            Toaster.error(data.message, 'warning')
          }
        })
        .catch(() => {
          Toaster.error('An eror occur, please try again later', 'warning')
        })
    }

    const getRecipients = recipients => {
      return recipients.map(opt => opt.recipient?.email).join(',')
    }

    return {
      // Single Message Actions
      singleMessageActions,
      openReplyBox,
      // UI Filters
      formatDate,
      reply,
      replyAll,
      discard,
      getRecipients,
      isVisible,
      replyMessage,
      download,
      customToolbar,
      composeData,
      loading,
      // Icons
      icons: {
        mdiAttachment,
        mdiStarOutline,
        mdiStar,
        mdiDotsVertical,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.email-message-card {
  transition: 0.1s all ease-in-out;
  will-change: top, position, position, transform-origin, height, overflow, top, z-index, opacity,
    transform;
}

.message-content > div ~ div,
br {
  display: none !important;
}

.message-content hr {
  display: none !important;
}
</style>
